import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { chatApi } from '../api/chat';
import CallTrackingTab from './CallTrackingTab';
import ChatbotLeadsSubTab from './ChatbotLeadsSubTab';
import WebformLeadsSubTab from './WebformLeadsSubTab';

type Props = { af: string | null };

type SubTabId = 'calls-tracking' | 'calls-webform' | 'calls-chatbot';

const SUBS: ReadonlyArray<{ id: SubTabId; label: string }> = [
  { id: 'calls-tracking', label: 'Call Tracking' },
  { id: 'calls-webform', label: 'Webform Leads' },
  { id: 'calls-chatbot', label: 'Chatbot Leads' },
];

export default function CallsAndLeadsTab({ af }: Props) {
  const [sub, setSub] = useState<SubTabId>('calls-tracking');

  // Same key as the Chat tab's gate, so this shares that cache rather than
  // asking again. The tab itself is now shown to everyone: a franchise without
  // the chatbot gets the Digital AirStrike panel here instead of leads, because
  // that is where their website chat still runs. The Chat *tab* stays gated —
  // this is the "where are my chat leads today" answer, not the feature.
  //
  // Deliberately NOT short-circuited for admins. The Chat tab's gate exempts
  // them because that one asks "may this user reach the feature", and the
  // answer there is yes for any location. This asks "where do THIS franchise's
  // chat leads land", which is a fact about the location and the same answer
  // whoever is looking. Exempting admins meant an admin viewing a location that
  // still runs Digital AirStrike saw an empty leads table reading "nobody
  // contacted you" — the exact thing the panel exists to prevent, and invisible
  // to the people who would notice it was wrong.
  const { data: chatEnabled, isLoading: chatStateLoading } = useQuery({
    queryKey: ['chat-enabled', af],
    queryFn: () => chatApi.isEnabled(af as string).catch(() => ({ enabled: false })),
    enabled: Boolean(af),
    staleTime: 5 * 60 * 1000,
  });
  const chatOn = chatEnabled?.enabled === true;

  const subs = SUBS;
  const active: SubTabId = sub;

  return (
    <>
      <div className="perf-sub-tabs">
        {subs.map((s) => (
          <button
            key={s.id}
            className={`perf-sub-btn ${active === s.id ? 'active' : ''}`}
            onClick={() => setSub(s.id)}
          >
            {s.label}
          </button>
        ))}
      </div>

      {active === 'calls-tracking' && <CallTrackingTab af={af} />}
      {active === 'calls-webform' && <WebformLeadsSubTab af={af} />}
      {/* Hold the subtab until we know which panel is the right one. Rendering
          on an unresolved query would flash "your leads are in Digital
          AirStrike" at a franchise that does have the chatbot — worse than a
          moment of nothing, because it is a confident wrong answer. */}
      {active === 'calls-chatbot' && (
        chatStateLoading && af
          ? <div className="tab-placeholder"><h3>Loading chatbot leads…</h3></div>
          : <ChatbotLeadsSubTab af={af} chatOn={chatOn} />
      )}
    </>
  );
}
